import type { GridMap, Entity, Trigger, TriggerEffect } from '@omen-quest/grid-map';
import type { DungeonMapData } from '@/lib/db/dungeon-types';
import { buildGridFromMapData } from './grid-builder';
import { apiFetch } from '@/lib/api';
import dungeonCrawlerMap from './dungeons/dungeon-crawler.json';

const DUNGEON_MAPS: Record<string, GridMap> = {
  'dungeon-crawler': dungeonCrawlerMap as unknown as GridMap,
};

/** Load a pre-generated dungeon map by ID. Throws if not found. */
export function loadDungeonMap(dungeonMapId: string): GridMap {
  const map = DUNGEON_MAPS[dungeonMapId];
  if (!map) throw new Error(`Unknown dungeon map: ${dungeonMapId}`);
  return structuredClone(map);
}

// ============================================================================
// API-BASED DUNGEON LOADER
// ============================================================================

/** Convert a DungeonTrigger effect string + effectData to the TriggerEffect union type. */
function buildTriggerEffect(
  effect: DungeonMapData['triggers'][number]['effect'],
  effectData: Record<string, unknown>,
): TriggerEffect {
  switch (effect) {
    case 'dialogue':
      return {
        type: 'dialogue',
        text: (effectData.text as string) ?? '',
        speaker: effectData.speaker as string | undefined,
      };
    case 'combat':
      return {
        type: 'combat',
        encounterId: (effectData.encounterId as string) ?? '',
      };
    case 'trap':
      return {
        type: 'trap',
        damage: effectData.damage as number | undefined,
        description: effectData.description as string | undefined,
      };
    case 'teleport':
      return {
        type: 'teleport',
        targetX: (effectData.targetX as number) ?? 0,
        targetY: (effectData.targetY as number) ?? 0,
      };
    case 'custom':
    default:
      return {
        type: 'custom',
        eventName: (effectData.eventName as string) ?? effect,
        data: effectData,
      };
  }
}

/**
 * Load a dungeon from the API by dungeon ID.
 * Fetches GET /api/dungeons/:id, builds the Cell[][] grid, converts entities and
 * triggers into GridMap format, and returns a complete GridMap object.
 */
export async function loadDungeonFromApi(dungeonId: string): Promise<GridMap> {
  const res = await apiFetch(`/api/dungeons/${dungeonId}`);
  const { dungeon } = await res.json();

  const mapData = dungeon.mapData as DungeonMapData;

  // Build the cell grid from room/corridor data
  const cells = buildGridFromMapData(mapData);

  // Convert dungeon entities → GridMap Entity format
  const entities: Entity[] = mapData.entities.map((e) => ({
    id: e.id,
    x: e.x,
    y: e.y,
    type: e.type as Entity['type'],
    name: e.name,
    icon: e.icon,
    metadata: e.metadata,
  }));

  // Convert dungeon triggers → GridMap Trigger format
  const triggers: Trigger[] = mapData.triggers.map((t) => {
    const triggerCells: Array<{ x: number; y: number }> = [];

    // If the trigger has explicit target coords, use those as the trigger cell
    if (t.targetX !== undefined && t.targetY !== undefined) {
      triggerCells.push({ x: t.targetX, y: t.targetY });
    } else if (t.targetEntityId) {
      // Find the entity and use its position
      const entity = mapData.entities.find((e) => e.id === t.targetEntityId);
      if (entity) {
        triggerCells.push({ x: entity.x, y: entity.y });
      }
    } else if (t.roomId) {
      // Fall back to room center
      const room = mapData.rooms.find((r) => r.id === t.roomId);
      if (room) {
        triggerCells.push({
          x: Math.floor(room.x + room.width / 2),
          y: Math.floor(room.y + room.height / 2),
        });
      }
    }

    return {
      id: t.id,
      cells: triggerCells,
      condition: t.condition,
      effect: buildTriggerEffect(t.effect, t.effectData),
      oneShot: t.oneShot,
      requiredFlags: t.requiredFlags.length > 0 ? t.requiredFlags : undefined,
      setsFlag: t.setsFlag ?? undefined,
    };
  });

  return {
    width: mapData.gridWidth,
    height: mapData.gridHeight,
    cells,
    entities,
    triggers,
    name: dungeon.name as string,
    metadata: {},
  };
}
